<template>
  <div class="single-photo">
    <!-- Photo loaded successfully -->
    <template v-if="photo && !loading && !error">
      <Photo :photo="photo" @like="like" @bookmark="bookmark">
        <template #header>
          <button
            @click="back"
            class="flex gap-1 items-center"
            :aria-label="$t('single_photo.back_button_label')"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
              <path
                fill="none"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m12 19l-7-7l7-7m7 7H5"
              />
            </svg>
          </button>
        </template>
      </Photo>
    </template>

    <!-- Error state -->
    <div v-else-if="error && !loading" class="error-container">
      <div class="error-content">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="64"
          height="64"
          viewBox="0 0 24 24"
          class="error-icon"
        >
          <path
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9s4.03-9 9-9s9 4.03 9 9z"
          />
        </svg>
        <h2 class="error-title">{{ $t('single_photo.load_failed_title') }}</h2>
        <p class="error-message">{{ $t('single_photo.load_failed_message') }}</p>
        <div class="button-group">
          <button @click="retryLoadPhoto" class="retry-button">
            {{ $t('single_photo.retry_load') }}
          </button>
          <button @click="back" class="back-button">
            {{ $t('single_photo.back_button_label') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-else-if="loading" class="loading-container">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <p class="loading-message">{{ $t('single_photo.loading_photo') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { usePhotos, type Photo as PhotoType } from '@/stores/photos'
import { useSupabase } from '@/stores/supabase'
import { useRouter } from 'vue-router'
import { useRoute } from 'vue-router'
import { ref, onMounted, computed } from 'vue'
import Photo from '@/components/Photo.vue'
import { useI18n } from 'vue-i18n'
import { useHead } from '@vueuse/head'

const { t } = useI18n()
const photos = usePhotos()
const supabase = useSupabase()
const router = useRouter()
const route = useRoute()

const photo = ref<PhotoType | null>(null)
const loading = ref(true)
const error = ref(false)

// Dynamic meta tags for photo
const metaTitle = computed(() =>
  photo.value ? `${photo.value.username}'s photo for "${photo.value.word}" on Gaze` : 'Photo - Gaze'
)
const metaDescription = computed(() =>
  photo.value
    ? photo.value.description
      ? `${photo.value.description} - Photo by ${photo.value.username} for the word "${photo.value.word}"`
      : `Photo by ${photo.value.username} for the word "${photo.value.word}" on Gaze`
    : 'View photo on Gaze'
)
const metaImage = computed(() => photo.value?.url || 'https://gaze.lindsaykwardell.com/logo.png')
const metaUrl = computed(() => `https://gaze.lindsaykwardell.com/photos/${route.params.photo_id}`)

useHead({
  title: metaTitle,
  meta: [
    { name: 'description', content: metaDescription },
    // Open Graph / Facebook
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: metaUrl },
    { property: 'og:title', content: metaTitle },
    { property: 'og:description', content: metaDescription },
    { property: 'og:image', content: metaImage },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'article:author', content: computed(() => photo.value?.username || '') },
    // Twitter
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:url', content: metaUrl },
    { name: 'twitter:title', content: metaTitle },
    { name: 'twitter:description', content: metaDescription },
    { name: 'twitter:image', content: metaImage },
    {
      name: 'twitter:creator',
      content: computed(() => (photo.value?.username ? `@${photo.value.username}` : ''))
    }
  ]
})

const back = () => {
  router.back()
}

async function like() {
  if (photo.value && supabase.user.status === 'SignedIn') {
    const success = await photos.like(photo.value.photo_id)

    if (success) {
      if (photo.value.liked) {
        photo.value.likes--
        photo.value.liked = false
      } else {
        photo.value.likes++
        photo.value.liked = true
      }
    } else {
      // Like failed - could be due to timing restrictions
      // The Photo component will handle the visual feedback
    }
  }
}

async function bookmark() {
  if (photo.value && supabase.user.status === 'SignedIn') {
    await photos.bookmark(photo.value.photo_id)

    photo.value.bookmarked = !photo.value.bookmarked
  }
}

async function loadPhoto() {
  loading.value = true
  error.value = false

  try {
    const { data, error: apiError } = await supabase.api
      .from('album')
      .select('*')
      .eq('photo_id', route.params.photo_id)
      .single()

    if (apiError) {
      console.error('Failed to load photo:', apiError)
      error.value = true
      photo.value = null
    } else if (data) {
      photo.value = data
      error.value = false
    } else {
      // No data returned but no error - photo not found
      error.value = true
      photo.value = null
    }
  } catch (err) {
    console.error('Network error loading photo:', err)
    error.value = true
    photo.value = null
  } finally {
    loading.value = false
  }
}

function retryLoadPhoto() {
  loadPhoto()
}

onMounted(() => {
  loadPhoto()
})
</script>

<style scoped>
.single-photo {
  height: calc(100dvh - 65px);
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  overflow: hidden;
}

.error-container,
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: theme('spacing.6');
  background: theme('colors.stone.50');

  .dark & {
    background: theme('colors.stone.950');
  }
}

.error-content,
.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: theme('spacing.6');
  text-align: center;
  max-width: 400px;
}

.error-icon {
  color: theme('colors.red.500');

  .dark & {
    color: theme('colors.red.400');
  }
}

.error-title {
  font-size: theme('fontSize.2xl');
  font-weight: theme('fontWeight.bold');
  color: theme('colors.stone.900');
  margin: 0;

  .dark & {
    color: theme('colors.stone.100');
  }
}

.error-message,
.loading-message {
  font-size: theme('fontSize.base');
  color: theme('colors.stone.600');
  margin: 0;
  line-height: theme('lineHeight.relaxed');

  .dark & {
    color: theme('colors.stone.400');
  }
}

.button-group {
  display: flex;
  gap: theme('spacing.3');
  flex-wrap: wrap;
  justify-content: center;
}

.retry-button,
.back-button,
.back-button-loading {
  border: none;
  border-radius: theme('borderRadius.lg');
  padding: theme('spacing.3') theme('spacing.6');
  font-size: theme('fontSize.base');
  font-weight: theme('fontWeight.medium');
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-button {
  background: theme('colors.blue.600');
  color: white;

  &:hover {
    background: theme('colors.blue.700');
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px theme('colors.blue.200');
  }

  .dark &:focus {
    box-shadow: 0 0 0 3px theme('colors.blue.800');
  }
}

.back-button,
.back-button-loading {
  background: theme('colors.stone.200');
  color: theme('colors.stone.700');

  .dark & {
    background: theme('colors.stone.700');
    color: theme('colors.stone.200');
  }

  &:hover {
    background: theme('colors.stone.300');
    transform: translateY(-1px);

    .dark & {
      background: theme('colors.stone.600');
    }
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px theme('colors.stone.300');

    .dark & {
      box-shadow: 0 0 0 3px theme('colors.stone.600');
    }
  }
}

.retry-button:active,
.back-button:active,
.back-button-loading:active {
  transform: translateY(0);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid theme('colors.stone.200');
  border-top: 4px solid theme('colors.blue.600');
  border-radius: 50%;
  animation: spin 1s linear infinite;

  .dark & {
    border: 4px solid theme('colors.stone.700');
    border-top: 4px solid theme('colors.blue.500');
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
